import React, { memo, useMemo } from 'react';
import { ActionButton, Tooltip, TooltipTrigger } from '@adobe/react-spectrum';
import { useSelector } from 'react-redux';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import { LegendBetaMiniIcon } from 'app/assets/legend/LegendBetaMiniIcon';
import { LegendPrivateBetaMiniIcon } from 'app/assets/legend/LegendPrivateBetaMiniIcon';
import { RevenueDriverIcon } from 'app/assets/legend/RevenueDriverIcon';
import { ProductIconCompact } from 'app/components/TimelineCard/ProductIconCompact';
import { RiskLevelTooltipContent } from 'app/components/shared/RiskLevelTooltipContent';
import { getReleasePhaseIcon } from 'utils/helpers';
import { RISK_LEVELS } from 'utils/constants';
import {
  getItemRevenueImpact,
  hasValidRiskLevel,
  isEventLane,
  isKeyDateLane
} from '../utils/monthlyItemIconUtils';
import { selectTimelineIconsVisible } from '../store/selectors';
import {
  DelayedTooltipState,
  useDelayedTooltip
} from '../hooks/useDelayedTooltip';
import type { MonthlyTimelineItem } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

const RISK_ICON_SIZE = 16;

interface MonthlyItemIconsProps {
  item: MonthlyTimelineItem;
  laneLabel: string;
  tooltip?: DelayedTooltipState;
}

function getRiskLevelIcon(riskLevel?: string) {
  if (!hasValidRiskLevel(riskLevel)) return null;

  if (riskLevel === RISK_LEVELS.LOW) {
    return <LegendRiskLowIcon width={RISK_ICON_SIZE} height={RISK_ICON_SIZE} />;
  }
  if (riskLevel === RISK_LEVELS.MODERATE) {
    return (
      <LegendRiskModerateIcon width={RISK_ICON_SIZE} height={RISK_ICON_SIZE} />
    );
  }
  if (riskLevel === RISK_LEVELS.HIGH) {
    return (
      <LegendRiskHighIcon width={RISK_ICON_SIZE} height={RISK_ICON_SIZE} />
    );
  }
  if (riskLevel === RISK_LEVELS.IN_PLANNING) {
    return <LegendInPlanningIcon width={14} height={14} />;
  }

  return null;
}

function getProductTooltip(products?: string[] | string) {
  if (!products) return '';
  if (Array.isArray(products)) {
    return products.length > 1
      ? `${products[0]} +${products.length - 1} more`
      : products[0];
  }
  return products;
}

export const MonthlyItemIcons = memo(
  ({ item, laneLabel, tooltip: sharedTooltip }: MonthlyItemIconsProps) => {
    const localTooltip = useDelayedTooltip();
    const {
      openTooltip,
      setOpenTooltip,
      handleTooltipMouseEnter,
      handleTooltipMouseLeave
    } = sharedTooltip ?? localTooltip;

    const timelineIconsVisible = useSelector(selectTimelineIconsVisible);
    const iconsAllowedForLane = useMemo(
      () => !isEventLane(laneLabel) && !isKeyDateLane(laneLabel),
      [laneLabel]
    );

    const revenueImpact = useMemo(() => getItemRevenueImpact(item), [item]);
    const phaseIconType = getReleasePhaseIcon(item.releasePhase);
    const riskIcon = getRiskLevelIcon(item.riskLevel);
    const products = item.products;
    const hasProducts = Array.isArray(products)
      ? products.length > 0
      : Boolean(products);
    const issueId = item.issueId || item.id || '';

    if (!iconsAllowedForLane) return null;

    const hasAnyIcon =
      revenueImpact ||
      (item.releasePhase && phaseIconType) ||
      hasProducts ||
      Boolean(riskIcon);

    if (!hasAnyIcon) return null;

    return (
      <span
        className={`${styles.monthlyItemIcons} ${
          timelineIconsVisible ? '' : styles.monthlyItemIconsHidden
        }`}
        data-testid={`monthly-item-icons-${issueId}`}
        aria-hidden={!timelineIconsVisible}
        onClick={event => event.stopPropagation()}
      >
        {revenueImpact && (
          <TooltipTrigger
            isOpen={openTooltip === 'revenue-driver'}
            onOpenChange={isOpen => {
              if (!isOpen) {
                setOpenTooltip(null);
              }
            }}
            placement="top"
          >
            <div
              className={styles.monthlyItemIconTooltipWrapper}
              onMouseEnter={() => handleTooltipMouseEnter('revenue-driver')}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <ActionButton
                data-testid={`monthly-item-revenue-impact-${issueId}`}
                isQuiet
                UNSAFE_className={styles.monthlyItemIconButton}
                aria-label="Revenue Impact"
              >
                <RevenueDriverIcon width={14} height={16} />
              </ActionButton>
            </div>
            <Tooltip>Impacts Revenue Targets</Tooltip>
          </TooltipTrigger>
        )}

        {item.releasePhase && phaseIconType && (
          <TooltipTrigger
            isOpen={openTooltip === `label-${phaseIconType}`}
            onOpenChange={isOpen => {
              if (!isOpen) {
                setOpenTooltip(null);
              }
            }}
            placement="top"
          >
            <div
              className={styles.monthlyItemIconTooltipWrapper}
              onMouseEnter={() =>
                handleTooltipMouseEnter(`label-${phaseIconType}`)
              }
              onMouseLeave={handleTooltipMouseLeave}
            >
              <ActionButton
                data-testid={`monthly-item-label-${phaseIconType}-${issueId}`}
                isQuiet
                UNSAFE_className={styles.monthlyItemIconButton}
                aria-label={item.releasePhase}
              >
                {phaseIconType === 'beta' ? (
                  <LegendBetaMiniIcon fill="#147AF3" />
                ) : (
                  <LegendPrivateBetaMiniIcon fill="#147AF3" />
                )}
              </ActionButton>
            </div>
            <Tooltip>{item.releasePhase}</Tooltip>
          </TooltipTrigger>
        )}

        {hasProducts && (
          <TooltipTrigger
            isOpen={openTooltip === 'product'}
            onOpenChange={isOpen => {
              if (!isOpen) {
                setOpenTooltip(null);
              }
            }}
            placement="top"
          >
            <div
              className={styles.monthlyItemIconTooltipWrapper}
              onMouseEnter={() => handleTooltipMouseEnter('product')}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <ActionButton
                data-testid={`monthly-item-product-${issueId}`}
                isQuiet
                UNSAFE_className={styles.monthlyItemIconButton}
                aria-label="Product"
              >
                <ProductIconCompact products={products} />
              </ActionButton>
            </div>
            <Tooltip>{getProductTooltip(products)}</Tooltip>
          </TooltipTrigger>
        )}

        {riskIcon && (
          <TooltipTrigger
            isOpen={openTooltip === 'risk'}
            onOpenChange={isOpen => {
              if (!isOpen) {
                setOpenTooltip(null);
              }
            }}
            placement="top"
          >
            <div
              className={styles.monthlyItemIconTooltipWrapper}
              onMouseEnter={() => handleTooltipMouseEnter('risk')}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <ActionButton
                data-testid={`monthly-item-risk-level-${issueId}`}
                isQuiet
                UNSAFE_className={styles.monthlyItemIconButton}
                aria-label={`Risk Level: ${item.riskLevel}`}
              >
                {riskIcon}
              </ActionButton>
            </div>
            <Tooltip>
              <RiskLevelTooltipContent riskLevel={item.riskLevel} />
            </Tooltip>
          </TooltipTrigger>
        )}
      </span>
    );
  }
);
